import React from "react";
import { Form, Input, Select, Row, Col, Descriptions, Tag, theme } from "antd";
import { UserOutlined, MailOutlined, GlobalOutlined, LinkOutlined, PhoneOutlined } from "@ant-design/icons";
import {
  EmailProviderType,
  UserCreationMethod,
} from "../../Services/PracticeDataRequestService";
import { Utils } from "../../Utilities/Utils";
import { DEFAULT_NOREPLY_EMAIL } from "./PublicClientDataRequestFormConfig";

const { Option } = Select;

interface PracticeDataRequestContactInfoProps {
  requestData: any;
  isEditing: boolean;
}

const stripUkPhonePrefix = (raw: any) => {
  if (typeof raw !== "string") return raw;
  let cleaned = raw.replace(/[^\d\s]/g, "");
  const pure = cleaned.replace(/\s/g, "");
  if (pure.startsWith("+44")) {
    cleaned = pure.slice(3);
  } else if (pure.startsWith("0044")) {
    cleaned = pure.slice(4);
  } else if (pure.startsWith("0")) {
    cleaned = pure.slice(1);
  }
  const pureDigits = cleaned.replace(/\s/g, "");
  if (pureDigits.length > 10) {
    cleaned = pureDigits.slice(0, 10);
  }
  return cleaned;
};

const blockNonDigits = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (!/[\d\s]/.test(e.key)) {
    e.preventDefault();
  }
};

const PracticeDataRequestContactInfo: React.FC<
  PracticeDataRequestContactInfoProps
> = ({ requestData, isEditing }) => {
  const { token } = theme.useToken();

  return (
    <Row gutter={[16, 16]}>
      {/* 1. Practice Information Card */}
      <Col xs={24} lg={12}>
        <div
          style={{
            background: token.colorBgContainer,
            borderRadius: token.borderRadiusLG,
            padding: "16px 20px",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              paddingBottom: 12,
              marginBottom: 14,
              borderBottom: `1px solid ${token.colorBorderSecondary}`,
            }}
          >
            <div>
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: token.colorTextHeading,
                }}
              >
                Practice Information
              </span>
              <div
                style={{
                  fontSize: 11,
                  color: token.colorTextSecondary,
                  marginTop: 1,
                }}
              >
                Workspace branding, website, and general contact details.
              </div>
            </div>
          </div>

          {!isEditing ? (
            <Descriptions
              size="small"
              bordered={false}
              column={{ xs: 1, sm: 2 }}
              labelStyle={{
                color: token.colorTextSecondary,
                fontWeight: 500,
                fontSize: 12,
              }}
              contentStyle={{
                color: token.colorText,
                fontWeight: 500,
                fontSize: 13,
              }}
            >
              <Descriptions.Item label="Practice Name">
                {requestData?.practiceName || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Phone Number">
                {requestData?.practicePhoneNumber ? (
                  <span>
                    <PhoneOutlined style={{ marginRight: 4, color: token.colorTextTertiary }} />
                    +44 {requestData.practicePhoneNumber}
                  </span>
                ) : (
                  "—"
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {requestData?.publicEmailNoReply ? (
                  <span>
                    <MailOutlined style={{ marginRight: 4, color: token.colorTextTertiary }} />
                    {requestData.publicEmailNoReply}
                  </span>
                ) : (
                  "—"
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Website">
                {requestData?.website ? (
                  <a
                    href={
                      requestData.website.startsWith("http")
                        ? requestData.website
                        : `https://${requestData.website}`
                    }
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: token.colorPrimary }}
                  >
                    <GlobalOutlined style={{ marginRight: 4 }} />
                    {requestData.website}
                  </a>
                ) : (
                  "—"
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Proposed URL" span={2}>
                {requestData?.proposedUrl ? (
                  <Tag color="blue" style={{ borderRadius: 4, margin: 0, fontWeight: 500 }}>
                    <LinkOutlined style={{ marginRight: 4 }} />
                    {requestData.proposedUrl}
                  </Tag>
                ) : (
                  "—"
                )}
              </Descriptions.Item>
            </Descriptions>
          ) : (
            <Row gutter={12}>
              <Col span={24}>
                <Form.Item
                  label="Practice Name"
                  name="practiceName"
                  rules={[
                    {
                      required: true,
                      message: "Practice Name is required",
                    },
                  ]}
                >
                  <Input placeholder="Enter practice name" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Phone number"
                  name="practicePhoneNumber"
                  initialValue="+44"
                  rules={[
                    {
                      required: true,
                      message: "Phone number is required",
                    },
                    {
                      validator: Utils.ValidateUkPhoneNumber,
                    },
                  ]}
                  getValueFromEvent={(e: any) =>
                    stripUkPhonePrefix(e?.target ? e.target.value : e)
                  }
                >
                  <Input
                    addonBefore="+44"
                    maxLength={10}
                    placeholder="Enter phone number"
                    onKeyPress={blockNonDigits}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Email"
                  name="publicEmailNoReply"
                  rules={[{ required: true, message: "Email is required" }]}
                >
                  <Input placeholder="Enter email address" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Website"
                  name="website"
                  rules={[{ required: true, message: "Website is required" }]}
                >
                  <Input placeholder="Enter website URL" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Proposed Workspace URL"
                  name="proposedUrl"
                  rules={[
                    {
                      required: true,
                      message: "Proposed Workspace URL is required",
                    },
                  ]}
                >
                  <Input placeholder="Enter proposed workspace URL" />
                </Form.Item>
              </Col>
            </Row>
          )}
        </div>
      </Col>

      {/* 2. Users & Emails Card */}
      <Col xs={24} lg={12}>
        <div
          style={{
            background: token.colorBgContainer,
            borderRadius: token.borderRadiusLG,
            padding: "16px 20px",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              paddingBottom: 12,
              marginBottom: 14,
              borderBottom: `1px solid ${token.colorBorderSecondary}`,
            }}
          >
            <div>
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: token.colorTextHeading,
                }}
              >
                Users & Emails
              </span>
              <div
                style={{
                  fontSize: 11,
                  color: token.colorTextSecondary,
                  marginTop: 1,
                }}
              >
                Primary contact, email provider, user creation method & authentication.
              </div>
            </div>
          </div>

          {!isEditing ? (
            <Descriptions
              size="small"
              bordered={false}
              column={{ xs: 1, sm: 2 }}
              labelStyle={{
                color: token.colorTextSecondary,
                fontWeight: 500,
                fontSize: 12,
              }}
              contentStyle={{
                color: token.colorText,
                fontWeight: 500,
                fontSize: 13,
              }}
            >
              <Descriptions.Item label="Contact Name">
                {requestData?.contactPersonName || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Contact Email">
                {requestData?.contactPersonEmail ? (
                  <span>
                    <MailOutlined style={{ marginRight: 4, color: token.colorTextTertiary }} />
                    {requestData.contactPersonEmail}
                  </span>
                ) : (
                  "—"
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Contact Phone">
                {requestData?.contactPersonPhone ? (
                  <span>
                    <PhoneOutlined style={{ marginRight: 4, color: token.colorTextTertiary }} />
                    +44 {requestData.contactPersonPhone}
                  </span>
                ) : (
                  "—"
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Invite Email">
                {requestData?.clientEmail || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Email Provider">
                <Tag color="cyan" style={{ borderRadius: 4, margin: 0, fontWeight: 500 }}>
                  {requestData?.emailProvider === 1
                    ? "Microsoft / Outlook"
                    : requestData?.emailProvider === 2
                      ? "Google"
                      : "Other"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="User Creation">
                <Tag color="purple" style={{ borderRadius: 4, margin: 0, fontWeight: 500 }}>
                  {requestData?.userCreationMethod === 2
                    ? "Create via SCIM"
                    : "Upload CSV"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="No-Reply Email" span={2}>
                {requestData?.customNoReplyEmail || `${DEFAULT_NOREPLY_EMAIL} (Default)`}
              </Descriptions.Item>
            </Descriptions>
          ) : (
            <Row gutter={12}>
              <Col span={24}>
                <Form.Item
                  label="Contact Person Name"
                  name="contactPersonName"
                  rules={[
                    {
                      required: true,
                      message: "Contact Person Name is required",
                    },
                  ]}
                >
                  <Input
                    placeholder="Enter contact person name"
                    prefix={
                      <UserOutlined
                        style={{ color: token.colorTextQuaternary }}
                      />
                    }
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Contact Email"
                  name="contactPersonEmail"
                  rules={[
                    {
                      required: true,
                      type: "email",
                      message: "Email is required",
                    },
                  ]}
                >
                  <Input
                    placeholder="Enter contact email address"
                    prefix={
                      <MailOutlined
                        style={{ color: token.colorTextQuaternary }}
                      />
                    }
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Contact Phone"
                  name="contactPersonPhone"
                  initialValue="+44"
                  rules={[
                    {
                      required: true,
                      message: "Contact phone number is required",
                    },
                    {
                      validator: Utils.ValidateUkPhoneNumber,
                    },
                  ]}
                  getValueFromEvent={(e: any) =>
                    stripUkPhonePrefix(e?.target ? e.target.value : e)
                  }
                >
                  <Input
                    addonBefore="+44"
                    maxLength={10}
                    placeholder="Enter contact phone number"
                    onKeyPress={blockNonDigits}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item label="Client Invite Email" name="clientEmail">
                  <Input disabled placeholder="Enter email address" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  label="Email Provider"
                  name="emailProvider"
                  initialValue={EmailProviderType.MicrosoftOutlook}
                >
                  <Select>
                    <Option value={EmailProviderType.MicrosoftOutlook}>
                      Microsoft / Outlook
                    </Option>
                    <Option value={EmailProviderType.Google}>Google</Option>
                    <Option value={EmailProviderType.Other}>Other</Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  label="No-Reply Email Address"
                  name="customNoReplyEmail"
                  tooltip={`Leave blank to use default ${DEFAULT_NOREPLY_EMAIL}`}
                  rules={[{ validator: Utils.ValidateEmailFormat }]}
                >
                  <Input placeholder={`${DEFAULT_NOREPLY_EMAIL} (Default)`} />
                </Form.Item>
              </Col>

              <Form.Item
                shouldUpdate={(prev, curr) =>
                  prev.emailProvider !== curr.emailProvider
                }
                noStyle
              >
                {({ getFieldValue }) => {
                  const provider = getFieldValue("emailProvider");
                  const isMicrosoft =
                    provider === EmailProviderType.MicrosoftOutlook;
                  return (
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="User Creation Method"
                        name="userCreationMethod"
                        initialValue={UserCreationMethod.UploadCsv}
                      >
                        <Select>
                          <Option value={UserCreationMethod.UploadCsv}>
                            Upload CSV
                          </Option>
                          <Option
                            value={UserCreationMethod.CreateViaSCIM}
                            disabled={!isMicrosoft}
                          >
                            Create via SCIM{" "}
                            {!isMicrosoft ? "(Microsoft only)" : ""}
                          </Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  );
                }}
              </Form.Item>
            </Row>
          )}
        </div>
      </Col>
    </Row>
  );
};

export default PracticeDataRequestContactInfo;
